import { useMemo } from 'react';
import { Empty } from 'antd';
import type { CallbackDataParams, ZRColor } from 'echarts/types/dist/shared';

import type { BarChartOption } from '../../../../models/echarts';
import { ReactECharts } from '../../../charts/ReactECharts';
import type { Status } from '../../client-profile/client-tabs/graphs/graphSlice';
import { getPercentage } from '../program-dashboard.helper';
import type { TimeInStatus } from '../programDashboardSlice';

const colorSpan = (color: ZRColor) =>
  '<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' +
  color +
  '"></span>';

const getOption = (data: TimeInStatus[]) => {
  if (!(data?.length > 0)) {
    return;
  }

  let total = 0;
  const minutesInStatusMap = data.reduce((acc, { cumulative_minutes_in_status }) => {
    for (const [status, value] of Object.entries(cumulative_minutes_in_status)) {
      acc.set(status as Status, (acc.get(status as Status) || 0) + value);
      total += value;
    }
    return acc;
  }, new Map<Status, number>());

  const option: BarChartOption = {
    grid: {
      top: '40%',
      left: '2%',
      right: '2%',
      bottom: '10%',
    },
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {},
      formatter: (params) => {
        const p = params as CallbackDataParams[];
        return p.map((e) => (e.color ? `${colorSpan(e.color)} ${e.seriesName}: ${e.data}% <br/>` : '')).join(' ');
      },
    },
    legend: {
      top: 15,
      left: '2%',
      right: '2%',
      itemWidth: 15,
    },
    color: ['#235580', '#1890ff', '#6FB2F0', '#bdb8ad', '#98A6AE', '#c6d4e1', '#ebe7e0', '#98A6AE'],
    xAxis: {
      show: false,
      type: 'value',
      max: 100,
    },
    yAxis: {
      show: false,
      type: 'category',
      data: [''],
    },
    series: [
      {
        type: 'bar',
        stack: 'Percentage',
        name: 'High',
        data: [getPercentage(minutesInStatusMap.get('high_risk'), total)],
      },
      {
        type: 'bar',
        stack: 'Percentage',
        name: 'Medium',
        data: [getPercentage(minutesInStatusMap.get('medium_risk'), total)],
      },
      {
        type: 'bar',
        stack: 'Percentage',
        name: 'Low',
        data: [getPercentage(minutesInStatusMap.get('low_risk'), total)],
      },
      {
        type: 'bar',
        stack: 'Percentage',
        name: 'Inactive',
        data: [getPercentage(minutesInStatusMap.get('inactive'), total)],
      },
    ],
  };

  return option;
};

export const DashboardChurroChartComponent = ({ data }: { data: TimeInStatus[] }) => {
  const option = useMemo(() => getOption(data), [data]);

  return option ? (
    <ReactECharts option={option} style={{ padding: 0, height: 120, width: '100%' }} />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 20 }} />
  );
};
